import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Focus, Eye, Target, Volume2, Clock, Zap, Brain } from 'lucide-react';
import { FocusActivityCard } from '../../components/FocusActivityCard';
import { FocusActivitySimulator } from '../../components/FocusActivitySimulator';
import { useProgress } from '../../hooks/useProgress';
import { useTodayProgress } from '../../hooks/useTodayProgress';

interface ChildContextType {
  selectedChild: string;
  selectedChildData: any;
  soundEnabled: boolean;
  theme: any;
}

interface FocusActivity {
  id: string;
  title: string;
  description: string;
  type: 'visual_tracking' | 'pattern_memory' | 'calm_breathing' | 'delay_gratification' | 'audio_sequencing' | 'stillness_focus';
  duration: number;
  difficulty: 'easy' | 'medium' | 'hard';
  points_reward: number;
}

// Sample focus activities
const focusActivities: FocusActivity[] = [
  {
    id: 'visual-1',
    title: 'Follow the Dot',
    description: 'Track a moving dot with your eyes to build visual focus skills.',
    type: 'visual_tracking',
    duration: 60,
    difficulty: 'easy',
    points_reward: 15
  },
  {
    id: 'pattern-1',
    title: 'Memory Patterns',
    description: 'Remember and repeat colorful patterns to strengthen your memory.',
    type: 'pattern_memory',
    duration: 90,
    difficulty: 'medium',
    points_reward: 20
  },
  {
    id: 'breathing-1',
    title: 'Calm Breathing',
    description: 'Breathe slowly with the growing circle to feel peaceful and focused.',
    type: 'calm_breathing',
    duration: 120,
    difficulty: 'easy',
    points_reward: 25
  },
  {
    id: 'patience-1',
    title: 'Wait for It',
    description: 'Practice patience by waiting for the perfect moment to click.',
    type: 'delay_gratification',
    duration: 45,
    difficulty: 'medium',
    points_reward: 18
  },
  {
    id: 'sounds-1',
    title: 'Sound Sequence',
    description: 'Listen carefully and repeat the sequence of sounds you hear.',
    type: 'audio_sequencing',
    duration: 75,
    difficulty: 'medium',
    points_reward: 22
  },
  {
    id: 'stillness-1',
    title: 'Mountain Stillness',
    description: 'Be as still and calm as a peaceful mountain.',
    type: 'stillness_focus',
    duration: 90,
    difficulty: 'hard',
    points_reward: 30
  }
];

export function ChildFocusPage() {
  const { selectedChild, selectedChildData, theme } = useOutletContext<ChildContextType>();
  const [selectedActivity, setSelectedActivity] = useState<FocusActivity | null>(null);
  const [completedActivities, setCompletedActivities] = useState<string[]>([]);
  const { addProgress } = useProgress(selectedChild);
  const { refetch: refetchTodayProgress } = useTodayProgress(selectedChild);

  const handleActivityComplete = async (score: number, timeSpent: number) => {
    if (!selectedActivity) return;

    const pointsEarned = Math.floor((score / 100) * selectedActivity.points_reward);

    // Add to progress tracking
    await addProgress({
      child_id: selectedChild,
      activity_id: selectedActivity.id,
      score,
      time_spent: timeSpent,
      points_earned: pointsEarned,
    });

    // Mark as completed
    setCompletedActivities(prev => [...prev, selectedActivity.id]);
    
    // Refresh today's progress
    refetchTodayProgress();
    
    // Close the activity
    setSelectedActivity(null);
  };

  const getActivityTypeIcon = (type: string) => {
    switch (type) {
      case 'visual_tracking': return Eye;
      case 'pattern_memory': return Target;
      case 'calm_breathing': return Volume2;
      case 'delay_gratification': return Clock;
      case 'audio_sequencing': return Volume2;
      case 'stillness_focus': return Focus;
      default: return Focus;
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case 'visual_tracking': return 'cyan';
      case 'pattern_memory': return 'purple';
      case 'calm_breathing': return 'green';
      case 'delay_gratification': return 'orange';
      case 'audio_sequencing': return 'blue';
      case 'stillness_focus': return 'teal';
      default: return 'blue';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4 text-[var(--text-primary)] font-baloo">
          Focus Area
        </h1>
        <p className="text-xl text-[var(--text-secondary)]">
          Build your focus superpowers with Neuro, {selectedChildData.name}!
        </p>
      </div>

      {/* Focus Introduction */}
      <div className={`rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl ${
        theme.mode === 'calm' 
          ? 'bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-600'
          : theme.mode === 'warm'
          ? 'bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-600'
          : theme.mode === 'high-contrast'
          ? 'bg-gradient-to-r from-cyan-600 to-teal-800 border-4 border-white'
          : 'bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600'
      }`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
        
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="p-4 bg-white bg-opacity-20 rounded-2xl">
              <Brain className="w-12 h-12" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2 font-baloo">
                Welcome to the Focus Area!
              </h2>
              <p className="text-lg opacity-90">
                Practice focusing, staying calm, and building your attention superpowers!
              </p>
            </div>
          </div>
          
          <div className="bg-white bg-opacity-15 rounded-2xl p-4 backdrop-blur-sm">
            <p className="text-lg font-baloo">
              "Hi {selectedChildData.name}! I'm Neuro, your brain buddy. These special activities will help you become a focus champion! Each one is like a mini-mission that makes your brain stronger and calmer." 🧠✨
            </p>
          </div>
        </div>
      </div>

      {/* Activity Types Overview */}
      <div>
        <h2 className="text-2xl font-bold text-center mb-6 text-[var(--text-primary)] font-baloo">
          Choose Your Focus Adventure
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {[
            { type: 'visual_tracking', name: 'Eye Tracking', icon: '👀', description: 'Follow with your eyes' },
            { type: 'pattern_memory', name: 'Memory Games', icon: '🧠', description: 'Remember patterns' },
            { type: 'calm_breathing', name: 'Calm Breathing', icon: '🌸', description: 'Breathe peacefully' },
            { type: 'delay_gratification', name: 'Patience Practice', icon: '⏰', description: 'Wait for the right moment' },
            { type: 'audio_sequencing', name: 'Sound Patterns', icon: '🎵', description: 'Listen and repeat' },
            { type: 'stillness_focus', name: 'Mountain Stillness', icon: '🏔️', description: 'Be calm and still' }
          ].map((activityType) => {
            const IconComponent = getActivityTypeIcon(activityType.type);
            const colorClass = getActivityTypeColor(activityType.type);
            
            return (
              <div
                key={activityType.type}
                className={`p-4 rounded-2xl text-center border-2 transition-all duration-200 ${
                  theme.reducedMotion ? '' : 'hover:scale-105'
                } ${
                  theme.mode === 'high-contrast'
                    ? `bg-[var(--accent-${colorClass})] text-white border-white`
                    : `bg-[var(--bg-secondary)] border-[var(--accent-${colorClass})] hover:bg-[var(--accent-${colorClass})] hover:bg-opacity-10`
                }`}
              >
                <div className="text-3xl mb-2">{activityType.icon}</div>
                <h3 className="font-bold text-[var(--text-primary)] font-baloo">{activityType.name}</h3>
                <p className="text-sm text-[var(--text-secondary)]">{activityType.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Focus Activities Grid */}
      <div>
        <h3 className="text-xl font-bold text-center mb-6 text-[var(--text-primary)] font-baloo">
          Ready to Focus? Pick an Activity!
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {focusActivities.map((activity) => (
            <FocusActivityCard
              key={activity.id}
              activity={activity}
              onStart={() => setSelectedActivity(activity)}
              completed={completedActivities.includes(activity.id)}
            />
          ))}
        </div>
      </div>

      {/* Encouragement Section */}
      <div className={`rounded-3xl p-8 text-white text-center shadow-2xl ${
        theme.mode === 'calm' 
          ? 'bg-gradient-to-r from-green-400 via-teal-500 to-cyan-600'
          : theme.mode === 'warm'
          ? 'bg-gradient-to-r from-amber-400 via-orange-500 to-red-500'
          : theme.mode === 'high-contrast'
          ? 'bg-gradient-to-r from-green-600 to-teal-800 border-4 border-white'
          : 'bg-gradient-to-r from-green-400 via-blue-500 to-purple-600'
      }`}>
        <h3 className="text-3xl font-bold mb-4 font-baloo">You're Building Focus Superpowers!</h3>
        <p className="text-xl leading-relaxed opacity-90">
          Every time you practice focusing, you're making your brain stronger and calmer! 
          Keep going, {selectedChildData.name} - you're becoming a focus champion! 🌟🧠✨
        </p>
      </div>

      {/* Focus Activity Simulator Modal */}
      {selectedActivity && (
        <FocusActivitySimulator
          activity={selectedActivity}
          onComplete={handleActivityComplete}
          onClose={() => setSelectedActivity(null)}
        />
      )}
    </div>
  );
}